/**
 * Skip Link Component
 * 
 * An accessibility component that provides a keyboard-accessible way to skip
 * to the main content of the page. Only visible when using keyboard navigation
 * (Tab key) and focuses on the main content area when activated.
 * 
 * Features:
 * - Keyboard-only visibility
 * - Focus management
 * - Screen reader support
 * - WCAG compliance
 */

'use client';

import { useEffect, useState } from 'react';

/**
 * Skip Link Component
 * 
 * @component
 * @returns {JSX.Element | null} The skip link component or null if not visible
 * 
 * @example
 * <SkipLink />
 */
export function SkipLink() {
	const [isVisible, setIsVisible] = useState(false);

	/**
	 * Handles keyboard navigation visibility
	 * Shows the skip link when Tab key is pressed
	 * @effect
	 */
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Tab') {
				setIsVisible(true);
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, []);

	if (!isVisible) return null;

	return (
		<a
			href="#main-content"
			className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:p-4 focus:bg-background focus:text-foreground focus:rounded-md focus:shadow-lg"
			onClick={() => setIsVisible(false)}
		>
			Skip to main content
		</a>
	);
}
